/**
 * WebSocket client for a booking's real-time channel (chat + live location).
 *
 * Connects to  ws(s)://<host>/ws/booking/<id>/?token=<JWT>  — the same endpoint
 * the mobile app uses. Auto-reconnects with backoff, and re-auths with the
 * current access token on each (re)connect so a refreshed token is picked up.
 */

// Derive the WS origin (absolute — required by the WebSocket constructor).
// Priority: explicit REACT_APP_WS_URL → absolute REACT_APP_API_URL → the page's
// own origin. In production REACT_APP_API_URL is the relative "/api", so we fall
// back to window.location and match its scheme (wss on https).
function wsBaseUrl() {
  if (process.env.REACT_APP_WS_URL) {
    return process.env.REACT_APP_WS_URL.replace(/\/$/, '');
  }
  const api = process.env.REACT_APP_API_URL || '/api';
  if (/^https?:\/\//i.test(api)) {
    return api.replace(/^http/i, 'ws').replace(/\/api\/?$/, '');
  }
  const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${proto}//${window.location.host}`;
}

export class BookingSocket {
  /**
   * @param {number|string} bookingId
   * @param {Object} handlers - { onMessage, onLocation, onTracking, onStatus,
   *                              onOpen, onClose, onConnectionState }
   */
  constructor(bookingId, handlers = {}) {
    this.bookingId = bookingId;
    this.handlers = handlers;
    this.ws = null;
    this.closedByUser = false;
    this.reconnectAttempts = 0;
    this.reconnectTimer = null;
  }

  connect() {
    this.closedByUser = false;
    const token = localStorage.getItem('token');
    if (!token) return; // Nothing to authenticate with

    const url = `${wsBaseUrl()}/ws/booking/${this.bookingId}/?token=${encodeURIComponent(token)}`;

    let ws;
    try {
      ws = new WebSocket(url);
    } catch (e) {
      this._scheduleReconnect();
      return;
    }
    this.ws = ws;
    this._setState('connecting');

    ws.onopen = () => {
      this.reconnectAttempts = 0;
      this._setState('connected');
      this.handlers.onOpen?.();
    };

    ws.onmessage = (evt) => {
      let data;
      try {
        data = JSON.parse(evt.data);
      } catch {
        return;
      }
      switch (data.type) {
        case 'chat_message':
        case 'photo_shared':
          this.handlers.onMessage?.(data);
          break;
        case 'location_update':
          this.handlers.onLocation?.(data);
          break;
        case 'tracking_status':
          this.handlers.onTracking?.(data);
          break;
        case 'connection_established':
          // Initial state includes the current tracking status + last point.
          if (data.tracking) this.handlers.onTracking?.({
            active: data.tracking.active,
            last_location: data.tracking.last_location,
            initial: true,
          });
          break;
        case 'status_change':
          this.handlers.onStatus?.(data);
          break;
        default:
          break;
      }
    };

    ws.onclose = () => {
      this._setState('disconnected');
      this.handlers.onClose?.();
      if (!this.closedByUser) this._scheduleReconnect();
    };

    ws.onerror = () => {
      // onclose fires next; reconnect is handled there.
      try { ws.close(); } catch { /* noop */ }
    };
  }

  _scheduleReconnect() {
    if (this.closedByUser) return;
    // Backoff: 1s, 2s, 4s … capped at 30s.
    const delay = Math.min(1000 * 2 ** this.reconnectAttempts, 30000);
    this.reconnectAttempts += 1;
    clearTimeout(this.reconnectTimer);
    this.reconnectTimer = setTimeout(() => this.connect(), delay);
  }

  _setState(state) {
    this.handlers.onConnectionState?.(state);
  }

  _send(obj) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(obj));
      return true;
    }
    return false;
  }

  sendChat(message) {
    return this._send({ type: 'chat_message', message });
  }

  sendTyping(isTyping) {
    return this._send({ type: 'typing', is_typing: isTyping });
  }

  // Handler-only; the server ignores these from non-handlers.
  startTracking() {
    return this._send({ type: 'tracking_start' });
  }

  stopTracking() {
    return this._send({ type: 'tracking_stop' });
  }

  sendLocation(latitude, longitude) {
    return this._send({
      type: 'location_update',
      latitude,
      longitude,
      timestamp: new Date().toISOString(),
    });
  }

  disconnect() {
    this.closedByUser = true;
    clearTimeout(this.reconnectTimer);
    if (this.ws) {
      try { this.ws.close(); } catch { /* noop */ }
      this.ws = null;
    }
  }
}

export default BookingSocket;
